import { useMemo, useState } from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import { ChevronDown, useQueryState, useNavigate, useI18n } from '@ywwwtseng/react-kit';
import { Typography } from '@ywwwtseng/tma-react-kit';
import { Select } from '@/components/ui/Select';
import { TokenAmount } from './TokenAmount';
import { Button } from '@ywwwtseng/react-kit';
import { Plus } from 'lucide-react';
import { useTokens } from '@/hooks/useTokens';
import { NETWORKS, NETWORK_ICONS, NETWORK_NAMES, MOONINI_TOKEN_ID } from '@/constants';
import { TokenIcon } from './TokenIcon';
import { getTokenSymbol } from '@/utils/token';

export function SelectToken({
  hideMoonini = false,
  readOnly = false,
  hideBalance = false,
  disabled = false,
  network: defaultNetwork,
  canAddToken = true,
  canChangeNetwork = true,
  value,
  onChange,
}: {
  hideMoonini?: boolean;
  readOnly?: boolean;
  hideBalance?: boolean;
  disabled?: boolean;
  network?: string | null;
  canAddToken?: boolean;
  canChangeNetwork?: boolean;
  value: string | null;
  onChange: (value: string) => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const tokens = useTokens();
  const networks = useQueryState<string[]>('networks');
  const [selectedNetwork, setSelectedNetwork] = useState<string>(
    defaultNetwork || NETWORKS.BSC
  );
  const network = useMemo(() => {
    return canChangeNetwork ? selectedNetwork : defaultNetwork;
  }, [selectedNetwork, canChangeNetwork, defaultNetwork]);

  const options = useMemo(() => {
    return [
      ...(tokens
        ?.filter((token) => token.network === network)
        .filter((token) => !hideMoonini || token.id !== MOONINI_TOKEN_ID)
        .map((token) => ({
          name: getTokenSymbol(token) ?? '',
          key: token.id,
          icon: <TokenIcon token={token} width={40} height={40} />,
          description: hideBalance ? undefined : <TokenAmount token={token} />,
        })) ?? []),
    ];
  }, [tokens, network, hideBalance]);

  const selected = useMemo(() => {
    return tokens?.find((token) => token.id === value);
  }, [options, value]);

  if (readOnly) {
    return (
      <div
        className={clsx(
          'w-full flex items-center justify-between gap-1 pl-2 pr-3 bg-form-element rounded-lg text-sm h-10',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div className="flex items-center gap-2">
          {selected?.icon && (
            <div className="scale-75">
              <TokenIcon token={selected} width={40} height={40} />
            </div>
          )}
          <Typography
            size="2"
            className={value ? 'text-foreground' : 'text-placeholder'}
          >
            {getTokenSymbol(selected) ?? t('common.token')}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <Select
      searchable
      trigger={
        <div
          className={clsx(
            'w-full flex items-center justify-between gap-1 pl-2 pr-3 bg-form-element rounded-lg text-sm h-10',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <div className="flex items-center gap-2">
            {selected?.icon && (
              <div className="scale-75">
                <TokenIcon token={selected} width={40} height={40} />
              </div>
            )}
            <Typography
              size="2"
              className={value ? 'text-foreground' : 'text-placeholder'}
            >
              {getTokenSymbol(selected) ?? t('common.token')}
            </Typography>
          </div>
          <ChevronDown width={20} height={20} />
        </div>
      }
      disabled={disabled}
      action={
        canAddToken && (
          <Button
            variant="icon"
            color="primary"
            size="sm"
            onClick={() => navigate('AddToken')}
          >
            <Plus width={20} height={20} />
          </Button>
        )
      }
      contentTop={
        !defaultNetwork && (
          <div className="w-full flex flex-col gap-2">
            <Typography size="1">{`${t('common.network')}: ${NETWORK_NAMES[network as keyof typeof NETWORK_NAMES]}`}</Typography>
            <div className="w-full flex items-center justify-start gap-4">
              {networks?.map((network) => (
                <div key={network} className={clsx('cursor-pointer rounded-xl p-2 bg-[#202020]', selectedNetwork === network && 'bg-white/70')} onClick={() => setSelectedNetwork(network)}>
                  <Image
                    src={NETWORK_ICONS[network as keyof typeof NETWORK_ICONS]}
                    alt={network}
                    width={24}
                    height={24}
                    className="rounded-full"
                  />
                </div>
              ))}
            </div>
          </div>)
      }
      value={value}
      options={options}
      onChange={(value) => {
        onChange(value);
      }}
    />
  );
}
